/**
 * Twitch API static configuration
 */
export const twitchStaticConfig = {
    baseUrl: 'https://api.twitch.tv/helix/',
    tokenUrl: 'https://id.twitch.tv/oauth2/token',
    oauthUrl: 'https://id.twitch.tv/oauth2/authorize',
    revokeUrl: 'https://id.twitch.tv/oauth2/revoke',
    callbackPath: '/api/auth/twitch/callback',
    /** Callback for the separate Poppy bot app. Different client ID. */
    botCallbackPath: '/api/auth/twitch/bot/callback',
    /**
     * Where Twitch delivers EventSub notifications for the bot app. Sits
     * under the webhook mount, not the auth routes, because it is signed inbound
     * traffic rather than an OAuth redirect.
     */
    eventSubCallbackPath: '/api/webhook/twitch/eventsub',
    /** The bot's Twitch account. Hosts mod this login to lift Poppy's rate limit. */
    botLogin: 'poppy_airhead_games',
    /** Minimal scopes (e.g. player Twitch login). */
    scopes: ['user:read:email'],
    /**
     * Elevated Twitch host scopes (email + mod sync). Requested only when needed:
     * mod_scope re-auth, or link/login when the user already has blocked-users
     * or blocked-terms sync enabled. Twitch tokens only include scopes from that
     * authorize call — never overwrite an elevated token with an email-only one
     * while those features are on (login chains mod_scope instead).
     */
    hostScopes: [
        'user:read:email',
        'user:read:blocked_users',
        'moderation:read',
        'moderator:read:blocked_terms',
    ],
    /**
     * Granted once by the Poppy bot account to the bot app. `user:read:chat`
     * carries the chat EventSub subscription; `user:write:chat` the sends.
     * Never mix these with the site-login scopes above — different client.
     */
    botScopes: ['user:bot', 'user:read:chat', 'user:write:chat'],
    /**
     * Granted per host to the bot app so Poppy may speak in their channel.
     * Deliberately not in hostScopes: scopes bind to a client ID, and a grant
     * against the site-login client would not authorize the bot.
     */
    botChannelScopes: ['channel:bot'],
    rateLimit: {
        requestsPerMinute: 800,
        maxConcurrent: 10,
    },
    cache: {
        tokenCacheDuration: 55 * 60 * 1000,
        userCacheDuration: 5 * 60 * 1000,
        streamCacheDuration: 30 * 1000,
    },
};
/**
 * The things Poppy can do in a host's chat, each its own opt-in.
 *
 * One list so the settings shape, the update schema, and the "grant is gone,
 * turn everything off" sweep cannot drift apart. Adding a capability means
 * adding it here and giving it a gate at its own send site.
 */
export const poppyChatFeatures = [
    'autoPostJoinLink',
    'chatCommands',
];
/** Safe defaults for new accounts and legacy records missing a capability. */
export const defaultPoppyChatSettings = {
    autoPostJoinLink: false,
    chatCommands: false,
};
const _poppyChatFeatureListIsComplete = true;
void _poppyChatFeatureListIsComplete;
/**
 * The subset of the above that needs Poppy to *hear* the channel.
 *
 * `channel.chat.message` fires on every message in the channel, so the
 * subscription only exists while a host wants something read. Posting the join
 * link does not: a host who opted into one message must not be opted into
 * having their chat read by side effect.
 *
 * Chat commands need the inbound message stream; automatic room-link posts do
 * not. Future read-based capabilities belong here without changing lifecycle
 * code.
 */
export const poppyChatReadingFeatures = [
    'chatCommands',
];
/**
 * Capabilities that post links, so Poppy watches for chat removing them.
 *
 * The `channel.chat.message_delete` and `channel.chat.clear_user_messages`
 * events say only whose message a moderator or chat bot removed, never what
 * anyone wrote. So unlike the reading list above, this covers the join-link
 * post: watching removals does not read the host's chat.
 */
export const poppyChatLinkPostingFeatures = [
    'autoPostJoinLink',
    'chatCommands',
];
/**
 * Full OAuth callback URL. The owning runtime supplies environment facts.
 */
export function getTwitchCallbackUrl(options) {
    const path = options.path || twitchStaticConfig.callbackPath;
    if (options.isProduction) {
        return `https://api.airheadgames.com${path}`;
    }
    if (options.tunnelUrl) {
        const tunnel = new URL(options.tunnelUrl);
        if (tunnel.protocol !== 'https:') {
            throw new Error(`Tunnel URL must be HTTPS: ${options.tunnelUrl}`);
        }
        return `${tunnel.origin}${path}`;
    }
    const port = options.port || '3001';
    return `http://localhost:${port}${path}`;
}
//# sourceMappingURL=twitch.js.map